import {
  createContext,
  useContext,
  useLayoutEffect,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Pause, Play, RotateCcw, Volume2 } from "lucide-react";
import { LocalMediaOwner, type LocalMediaState } from "./local-media-owner.ts";
import "./local-audio.css";

interface MediaContextValue {
  owner: LocalMediaOwner;
  beforePlay: () => Promise<boolean>;
  unavailable: boolean;
}
const MediaContext = createContext<MediaContextValue | null>(null);
const permitLocalListening = async () => true;

export function LocalMediaProvider({
  children,
  scope,
  interrupted,
  unavailable = false,
  beforePlay = permitLocalListening,
  owner: providedOwner,
}: {
  children: ReactNode;
  scope: string;
  /** Changes when another presenter resumes shared playback. */
  interrupted?: string | number | boolean;
  unavailable?: boolean;
  beforePlay?: () => Promise<boolean>;
  owner?: LocalMediaOwner;
}) {
  const [ownOwner] = useState(() => new LocalMediaOwner());
  const owner = providedOwner ?? ownOwner;
  useLayoutEffect(() => {
    owner.stop();
    return owner.stop;
  }, [owner, scope, interrupted, unavailable]);
  useLayoutEffect(() => {
    const hidden = () => {
      if (document.hidden) owner.stop();
    };
    document.addEventListener("visibilitychange", hidden);
    return () => document.removeEventListener("visibilitychange", hidden);
  }, [owner]);
  return (
    <MediaContext.Provider value={{ owner, beforePlay, unavailable }}>
      {children}
    </MediaContext.Provider>
  );
}

export function useLocalMediaOwner() {
  return useContext(MediaContext)?.owner;
}

export function LocalAudioPlayer({
  identity,
  src,
  label,
  className = "",
  disabled = false,
  autoPlayKey,
  onEnded,
  onStateChange,
}: {
  identity: string;
  src: string;
  label: string;
  className?: string;
  disabled?: boolean;
  /** A server-held response may narrate once. Ordinary evidence players remain manual. */
  autoPlayKey?: string;
  onEnded?: () => void;
  onStateChange?: (state: LocalMediaState) => void;
}) {
  const context = useContext(MediaContext);
  const [fallbackOwner] = useState(() => new LocalMediaOwner());
  const owner = context?.owner ?? fallbackOwner;
  const [state, setState] = useState<LocalMediaState>("paused");
  const stateListener = useRef(onStateChange);
  stateListener.current = onStateChange;
  useEffect(() => {
    stateListener.current?.(state);
  }, [state, identity, src]);
  const audio = useRef<HTMLAudioElement>(null);
  const current = useRef({ identity, src, disabled });
  const completion = useRef(onEnded);
  completion.current = onEnded;
  const attempted = useRef<string | undefined>(undefined);
  current.current = {
    identity,
    src,
    disabled: disabled || Boolean(context?.unavailable),
  };
  useLayoutEffect(() => {
    setState("paused");
    const element = audio.current;
    return () => {
      if (element) owner.release(element);
    };
  }, [owner, identity, src]);
  useLayoutEffect(() => {
    if (current.current.disabled && audio.current) owner.release(audio.current);
  }, [owner, disabled, context?.unavailable]);
  const start = (automatic = false) => {
    const element = audio.current;
    if (!element || current.current.disabled) return;
    void owner.play({
      id: identity,
      media: element,
      beforePlay: automatic
        ? permitLocalListening
        : (context?.beforePlay ?? permitLocalListening),
      isCurrent: () =>
        current.current.identity === identity &&
        current.current.src === src &&
        !current.current.disabled &&
        element.isConnected &&
        !element.closest("[hidden]"),
      onState: (next) => {
        setState(next);
        if (next === "ended") completion.current?.();
      },
    });
  };
  useEffect(() => {
    if (!autoPlayKey || attempted.current === autoPlayKey) return;
    attempted.current = autoPlayKey;
    start(true);
    // This token is a single attempt, never a timer or a retry after navigation.
    const element = audio.current;
    return () => {
      if (element) owner.release(element);
    };
  }, [autoPlayKey]);
  const pending = state === "waiting" || state === "loading";
  const playing = state === "playing";
  const failure =
    state === "pause-failed"
      ? "Playback could not pause. Try again."
      : state === "play-failed"
        ? "Audio did not start. Select Play again."
        : state === "unavailable"
          ? "This recording is unavailable. You can still read the response."
          : "";
  const button = playing
    ? "Pause recording"
    : pending
      ? "Cancel audio"
      : state === "ended"
        ? "Replay recording"
        : state === "play-failed"
          ? "Play again"
          : "Play recording";
  return (
    <div
      className={`local-audio ${className}`}
      data-local-media
      data-media-identity={identity}
    >
      <audio ref={audio} src={src} preload="none" aria-label={label} />
      <button
        type="button"
        className="local-audio__button"
        aria-label={`${button}: ${label}`}
        disabled={disabled || context?.unavailable}
        onClick={() => {
          if (playing || pending) {
            owner.stop();
            return;
          }
          start();
        }}
      >
        {playing ? (
          <Pause size={16} aria-hidden />
        ) : state === "ended" ? (
          <RotateCcw size={16} aria-hidden />
        ) : pending ? (
          <Volume2 size={16} aria-hidden />
        ) : (
          <Play size={16} aria-hidden />
        )}
        <span>{pending ? "Cancel audio" : button}</span>
      </button>
      {failure && (
        <p className="local-audio__error" role="status">
          {failure}
        </p>
      )}
    </div>
  );
}
